import * as history from "history";
import { action } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { RouterStore, syncHistoryWithStore } from "./MobxReactRouter";
import { useCurrentPath } from "./useCurrentPath";
import { WSErrorContainer } from "../error/WSErrorContainer";

function modifyRegexp(regexp: RegExp) {
  let flags = regexp.flags;
  let source = regexp.source;

  const isValid = source.endsWith("$") || source.endsWith("\\/");

  if (!isValid) {
    throw new Error("Route regex must end with $ or \\/");
  }

  if (source.endsWith("$")) {
    // add optional trailing slash to the regex
    source = source.slice(0, source.length - 1) + "\\/?$";
  }

  if (!source.startsWith("^")) {
    // require the match to start at the beginning of the string
    source = "^" + source;
  }

  if (source.startsWith("^\\/")) {
    // remove required leading slash
    source = "^" + source.slice(2);
  }

  if (!source.startsWith("^\\/?")) {
    // add optional leading slash
    source = "^\\/?" + source.slice(1);
  }

  // add case insensitivity if not already present
  if (!flags.includes("i")) {
    flags += "i";
  }

  return new RegExp(source, flags);
}

export enum PathType {
  EXACT = "EXACT",
  SUBPATH = "SUBPATH",
  EXACT_OR_SUBPATH = "EXACT_OR_SUBPATH",
}

export class _Path {
  path: string;
  type: PathType;

  constructor(path: string, type: PathType) {
    this.path = path;
    this.type = type;
  }
}

export function path(path: string, type: PathType = PathType.EXACT) {
  return new _Path(path, type);
}

const convertPathToRegexp = (path: _Path) => {
  switch (path.type) {
    case PathType.EXACT:
      return [modifyRegexp(new RegExp(path.path + "$"))];
    case PathType.SUBPATH:
      return [modifyRegexp(new RegExp(path.path + "\\/"))];
    case PathType.EXACT_OR_SUBPATH:
      return [
        modifyRegexp(new RegExp(path.path + "$")),
        modifyRegexp(new RegExp(path.path + "\\/")),
      ];
  }
};

/**
 * Only accepts regexp that end with
 *
 * 1. $
 * Matches only the path
 *
 * 2. \\/
 * Matches all subpaths, but not the path itself
 */
export type IWsRoutePath = _Path[] | _Path;

export const wsRouteMatch = (matcher: IWsRoutePath, currentPath) => {
  if (!Array.isArray(matcher)) {
    matcher = [matcher as any];
  }

  const regexps = matcher.map((m) => convertPathToRegexp(m)).flat();

  const _currentPath = currentPath.toLowerCase();
  return !!regexps.find((regexp) => {
    return !!_currentPath.match(modifyRegexp(regexp));
  });
};

export interface WSRouteProps {
  path: IWsRoutePath;
  render?: any;
}

export const WSRoute = observer(({ path, render }: WSRouteProps) => {
  const currentPath = useCurrentPath();

  return (
    !!wsRouteMatch(path, currentPath) && (
      <React.Suspense fallback={null}>
        <WSErrorContainer>{render()}</WSErrorContainer>
      </React.Suspense>
    )
  );
});

// // just ideas for now
// function useDelayedNavigate(path: IWsRoutePath) {
//   const currentPath = useCurrentPath();
//   const isMatch = !!wsRouteMatch(path, currentPath);
//   const previousMatched = React.useRef(isMatch);

//   const { newlyMatched, newlyUnmatched } = React.useMemo(() => {
//     const change = isMatch !== previousMatched.current;

//     const newlyMatched = change && previousMatched.current;
//     const newlyUnmatched = change && previousMatched.current;

//     previousMatched.current = isMatch;

//     return { newlyMatched, newlyUnmatched };
//   }, [currentPath]);

//   const isDoneFetching = true; // TODO

//   return { newlyMatched, newlyUnmatched };
// }

export const routerStore = new RouterStore();

let _browserHistory: history.BrowserHistory = null;
export const setupRouterStore = () => {
  if (_browserHistory) return _browserHistory;

  /* const id_konsern = document
    .getElementById("hidKonsernId")
    .getAttribute("value"); */
  const browserHistory = history.createBrowserHistory({
    /*basename: `app/${id_konsern}`, */
  });

  _browserHistory = syncHistoryWithStore(browserHistory, routerStore);
  return _browserHistory;
};

setupRouterStore();

export const BrowserHistoryContext =
  React.createContext<history.BrowserHistory>(null);
